import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { get, post } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { parseTemplateEditorQuery, templateEditorHref } from '@/lib/template-editor-query';
import LineupGarmentPicker, { GarmentThumb, LocationChips } from '@/components/LineupGarmentPicker';
import { lineupPickerCards, thumbForAliases } from '@/lib/lineup-thumbs';
import {
  COLOR_RULES,
  brandsFromStyles,
  garmentStyleNumbers,
  isProductTemplate,
  newProductTemplateId,
  sortProductTemplates,
  styleFromGarment,
  type GarmentLike,
  type ProductStyle,
} from '@/lib/store-template-kinds';

// Product-lineup editor — the write side for storeTemplates/general, nike-only,
// adidas-only, …. Picks which SanMar / S&S / house blanks a NEW store may sell
// and the color rules color-match expands. No designs live here; those bind on
// Assign to Products (sport-*). Writes only to storeTemplates; never Shopify.

type Template = {
  id: string;
  name?: string;
  description?: string;
  kind?: string;
  brandTag?: string;
  styles?: ProductStyle[];
};

type Garment = GarmentLike;

const EMPTY_THUMBS: Record<string, string> = {};

export default function ProductTemplateEditor() {
  const { data: tplData, isLoading, mutate } = useSWR<{ templates?: Template[] }>('/api/store-templates', get);
  const { data: garmentData } = useSWR<{ garments?: Garment[] }>('/api/supplier-catalog/garments', get);
  const { data: thumbData } = useSWR<{ thumbs?: Record<string, string> }>('/api/lineup-garment-thumbs', get);

  const templates = sortProductTemplates(tplData?.templates || []);
  const garments = garmentData?.garments || [];

  const [searchParams] = useSearchParams();
  const deepLink = useMemo(() => parseTemplateEditorQuery(searchParams.toString()), [searchParams]);

  const [selectedId, setSelectedId] = useState<string | null>(deepLink.templateId || 'general');
  const [draft, setDraft] = useState<Template | null>(null);
  const [status, setStatus] = useState('');
  const [newStyleNumber, setNewStyleNumber] = useState(deepLink.styleNumber || '');
  const [newColors, setNewColors] = useState<Record<string, boolean>>({});
  const [highlightStyle, setHighlightStyle] = useState(deepLink.styleNumber);
  const [cloneName, setCloneName] = useState('');
  const [cloneDescription, setCloneDescription] = useState('');

  useEffect(() => {
    if (!templates.length) return;
    if (deepLink.templateId && templates.some((t) => t.id === deepLink.templateId)) {
      setSelectedId(deepLink.templateId);
    } else if (deepLink.styleNumber) {
      const hit = templates.find((t) => (t.styles || []).some((s) => s.styleNumber.toUpperCase() === deepLink.styleNumber));
      if (hit) setSelectedId(hit.id);
    }
    if (deepLink.styleNumber) {
      setNewStyleNumber(deepLink.styleNumber);
      setHighlightStyle(deepLink.styleNumber);
    }
  }, [tplData, deepLink.templateId, deepLink.styleNumber]);

  const selected = draft || templates.find((t) => t.id === selectedId) || null;
  const sidebar = useMemo(() => {
    if (draft && !templates.some((t) => t.id === draft.id)) return [draft, ...templates];
    return templates;
  }, [draft, templates]);
  const styleCount = (selected?.styles || []).length;
  const brands = brandsFromStyles(selected?.styles || []);

  const garmentLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of garments) {
      for (const n of garmentStyleNumbers(g)) map.set(n.toUpperCase(), g.name || n);
    }
    return (styleNumber: string) => map.get(styleNumber.toUpperCase()) || '';
  }, [garments]);

  const garmentByStyle = useMemo(() => {
    const map = new Map<string, Garment>();
    for (const g of garments) {
      for (const n of garmentStyleNumbers(g)) map.set(n.toUpperCase(), g);
    }
    return map;
  }, [garments]);

  const thumbs = thumbData?.thumbs || EMPTY_THUMBS;
  const pickerCards = useMemo(() => lineupPickerCards(garments, thumbs), [garments, thumbs]);
  const inLineup = useMemo(
    () => new Set((selected?.styles || []).map((s) => s.styleNumber.toUpperCase())),
    [selected],
  );

  function styleImage(styleNumber: string): string {
    const g = garmentByStyle.get(styleNumber.toUpperCase());
    return thumbForAliases([styleNumber, ...(g ? garmentStyleNumbers(g) : [])], thumbs);
  }

  function edit(mut: (t: Template) => void) {
    if (!selected) return;
    const base: Template = JSON.parse(JSON.stringify(selected));
    mut(base);
    setDraft(base);
  }

  function setStyleColors(si: number, rule: string, on: boolean) {
    edit((t) => {
      const s = t.styles![si];
      s.colors = { ...(s.colors || {}), [rule]: on };
    });
  }

  function setStyleField(si: number, field: keyof ProductStyle, value: string) {
    edit((t) => {
      (t.styles![si] as Record<string, unknown>)[field] = value || undefined;
    });
  }

  function pickStyle(styleNumber: string) {
    setNewStyleNumber(styleNumber);
    setNewColors((m) => (Object.values(m).some(Boolean) ? m : { black: true, primary: true }));
  }

  function addStyleWith(styleNumber: string, colorMap: Record<string, boolean>) {
    if (!styleNumber || !selected) return;
    if ((selected.styles || []).some((s) => s.styleNumber.toUpperCase() === styleNumber.toUpperCase())) {
      setNewStyleNumber(styleNumber);
      setStatus(`${styleNumber} is already in this lineup — edit its colors there.`);
      return;
    }
    const colors = Object.fromEntries(Object.entries(colorMap).filter(([, v]) => v));
    if (!Object.keys(colors).length) {
      setStatus('Pick at least one color rule before adding.');
      return;
    }
    const g = garmentByStyle.get(styleNumber.toUpperCase());
    const next = g
      ? styleFromGarment(g, colors)
      : { styleNumber, colors };
    if (g && !next.styleNumber) next.styleNumber = styleNumber;
    if (next.styleNumber !== styleNumber) next.styleNumber = styleNumber;
    edit((t) => {
      t.styles = t.styles || [];
      t.styles.push(next);
    });
    setNewStyleNumber(styleNumber);
    setStatus(`Added ${styleNumber} — Save when the lineup looks right.`);
  }

  function addStyle() {
    addStyleWith(newStyleNumber.trim(), newColors);
  }

  function quickAdd(styleNumber: string) {
    const colors = Object.values(newColors).some(Boolean) ? newColors : { black: true, primary: true };
    setNewColors(colors);
    addStyleWith(styleNumber, colors);
  }

  function removeStyle(si: number) {
    const s = (selected?.styles || [])[si];
    if (!s) return;
    if (!confirm(`Remove ${s.styleNumber} from this lineup? (Nothing changes until you Save.)`)) return;
    edit((t) => { t.styles!.splice(si, 1); });
  }

  function startClone(empty: boolean) {
    if (!selected) return;
    const name = cloneName.trim();
    const id = newProductTemplateId(name);
    if (!id) {
      setStatus(name.toLowerCase().includes('sport')
        ? 'Product lineup ids cannot start with sport- — that prefix is reserved for artwork templates.'
        : 'Enter a name (letters and numbers) for the new lineup.');
      return;
    }
    if ((tplData?.templates || []).some((t) => t.id === id) || (draft && draft.id === id)) {
      setStatus(`${id} already exists — pick a different name.`);
      return;
    }
    const next: Template = {
      id,
      name,
      description: cloneDescription.trim() || (empty ? '' : `Cloned from ${selected.id}`),
      kind: 'product',
      styles: empty ? [] : JSON.parse(JSON.stringify(selected.styles || [])),
    };
    setDraft(next);
    setSelectedId(id);
    setCloneName('');
    setCloneDescription('');
    setStatus(`Drafting ${id} — Save to write it to storeTemplates.`);
  }

  async function save() {
    if (!selected) return;
    if (selected.id.startsWith('sport-') || !isProductTemplate({ ...selected, active: true })) {
      setStatus('Refusing to save — this editor only writes product lineups, never sport-* artwork.');
      return;
    }
    if (!selected.name) {
      setStatus('Name is required to save.');
      return;
    }
    setStatus('saving…');
    try {
      await post('/api/store-templates', selected);
      setStatus('✓ saved — new stores pick this up; live stores are unchanged');
      setDraft(null);
      mutate();
    } catch (e) {
      setStatus('save failed: ' + (e as Error).message);
    }
  }

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Product lineups</h1>
      <p className="mb-4 text-[12px] text-muted">
        Which garments a <b>new</b> store may sell. Default is <b>general</b>. Brand lineups (nike-only, …) exclude other vendors.
        Click a photo to see logo locations, sizes, and style numbers, then add it.
        Designs bind on{' '}
        <Link to={templateEditorHref({})} className="underline">Assign to Products</Link>
        . Live stores are not rewritten.
      </p>

      <div className="grid items-start gap-5 lg:grid-cols-[240px_1fr]">
        <div className="rounded-md bg-surface p-3 shadow-card">
          {isLoading ? <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
            : templates.length ? (
              <div className="space-y-1">
                {sidebar.map((t) => (
                  <button key={t.id} onClick={() => { setSelectedId(t.id); setDraft(null); setStatus(''); setNewStyleNumber(''); setNewColors({}); }}
                    className={`flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-[12px] ${selectedId === t.id ? 'bg-primary text-white' : 'hover:bg-bg'}`}>
                    <span className="truncate">{t.name || t.id}</span>
                    <span className="text-[10px] opacity-70">{(t.styles || []).length}</span>
                  </button>
                ))}
              </div>
            ) : <div className="p-4 text-center text-[11px] text-muted">No product lineups yet.</div>}

          <div className="mt-3 space-y-2 border-t border-border pt-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted">New lineup</div>
            <input
              aria-label="New lineup name"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="Name (e.g. Nike only)"
              className="h-7 w-full rounded-sm border border-border bg-white px-1.5 text-[11px]"
            />
            <input
              aria-label="New lineup description"
              value={cloneDescription}
              onChange={(e) => setCloneDescription(e.target.value)}
              placeholder="Description (optional)"
              className="h-7 w-full rounded-sm border border-border bg-white px-1.5 text-[11px]"
            />
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => startClone(true)}
                disabled={!cloneName.trim()}
                className="rounded-sm border border-border bg-white px-2 py-1 text-[11px] hover:bg-bg disabled:opacity-50"
              >
                Empty
              </button>
              <button
                onClick={() => startClone(false)}
                disabled={!cloneName.trim() || !selected}
                className="rounded-sm border border-border bg-white px-2 py-1 text-[11px] hover:bg-bg disabled:opacity-50"
              >
                Clone current
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-md bg-surface p-4 shadow-card">
          {!selected ? <div className="p-6 text-center text-[12px] text-muted">Select a product lineup.</div> : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <input
                  aria-label="Lineup name"
                  value={selected.name || ''}
                  onChange={(e) => edit((t) => { t.name = e.target.value; })}
                  className="h-8 min-w-[180px] rounded-sm border border-border bg-white px-2 text-[14px] font-semibold"
                />
                <Badge className="bg-bg text-muted">{selected.id}</Badge>
                {brands.length > 0 && <Badge className="bg-primary/10 text-primary">{brands.join(', ')}</Badge>}
                <span className="text-[11px] text-muted">{styleCount} garments</span>
                <button onClick={save} disabled={!draft} className="ml-auto rounded-sm bg-primary px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-50">Save</button>
              </div>
              <input
                aria-label="Lineup description"
                value={selected.description || ''}
                onChange={(e) => edit((t) => { t.description = e.target.value; })}
                placeholder="What this lineup is for (rep-facing)"
                className="mb-3 h-7 w-full rounded-sm border border-border bg-white px-1.5 text-[11px]"
              />

              <div className="space-y-3">
                <LineupGarmentPicker
                  cards={pickerCards}
                  inLineup={inLineup}
                  selectedStyle={newStyleNumber}
                  onSelectStyle={pickStyle}
                  newColors={newColors}
                  onColors={setNewColors}
                  onAdd={addStyle}
                  onQuickAdd={quickAdd}
                  typedStyle={newStyleNumber}
                  onTypedStyle={pickStyle}
                  canAdd={!!newStyleNumber.trim() && Object.values(newColors).some(Boolean)}
                  addDisabledReason={!newStyleNumber.trim() ? 'Pick a garment' : !Object.values(newColors).some(Boolean) ? 'Pick at least one color rule' : undefined}
                />

                <div className="text-[11px] font-semibold text-text/80">
                  In this lineup <span className="font-normal text-muted">· {styleCount} garments</span>
                </div>
                {(selected.styles || []).map((s, si) => {
                  const card = pickerCards.find((c) => c.styleNumber.toUpperCase() === s.styleNumber.toUpperCase()
                    || c.aliases.some((a) => a.toUpperCase() === s.styleNumber.toUpperCase()));
                  return (
                  <div
                    id={`style-${s.styleNumber}`}
                    key={`${s.styleNumber}-${si}`}
                    className={`rounded-md border p-3 ${highlightStyle && s.styleNumber.toUpperCase() === highlightStyle ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}
                  >
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <GarmentThumb src={styleImage(s.styleNumber)} alt={garmentLabel(s.styleNumber) || s.styleNumber} className="h-14 w-14" />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-[12px] font-semibold">{s.styleNumber}</span>
                          {garmentLabel(s.styleNumber) && <span className="text-[11px] text-muted">{garmentLabel(s.styleNumber)}</span>}
                        </div>
                        <div className="mt-1">
                          <LocationChips locations={card?.locations || []} />
                        </div>
                      </div>
                      <button onClick={() => removeStyle(si)} className="ml-auto rounded-sm border border-border bg-white px-2 py-1 text-[11px] text-red-600 hover:bg-bg">remove</button>
                    </div>
                    <div className="mb-2 flex flex-wrap gap-2">
                      {COLOR_RULES.map((c) => (
                        <label key={c} className="flex items-center gap-1 text-[11px]">
                          <input type="checkbox" checked={!!s.colors?.[c]} onChange={(e) => setStyleColors(si, c, e.target.checked)} />
                          {c}
                        </label>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <input
                        aria-label={`${s.styleNumber} product title`}
                        value={s.productTitle || ''}
                        onChange={(e) => setStyleField(si, 'productTitle', e.target.value)}
                        placeholder="Title"
                        className="h-7 min-w-[140px] flex-1 rounded-sm border border-border bg-white px-1.5 text-[11px]"
                      />
                      <input
                        aria-label={`${s.styleNumber} brand or supplier`}
                        value={s.brand || s.supplier || ''}
                        onChange={(e) => setStyleField(si, 'brand', e.target.value)}
                        placeholder="Brand / supplier"
                        className="h-7 w-36 rounded-sm border border-border bg-white px-1.5 text-[11px]"
                      />
                      <input
                        aria-label={`${s.styleNumber} category`}
                        value={s.category || ''}
                        onChange={(e) => setStyleField(si, 'category', e.target.value)}
                        placeholder="Category"
                        className="h-7 w-28 rounded-sm border border-border bg-white px-1.5 text-[11px]"
                      />
                    </div>
                  </div>
                  );
                })}
              </div>
              <div className="mt-3 text-[11px] text-muted">{status}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
